import classNames from "classnames";

import { Tooltip } from "../../../general/Tooltip";
import { LinkActionGlyph } from "../components/Icons";
import { isSafeHref } from "../utils/urlSafety";

import CSS from "../styles.module.css";

export type DialogMode = "edit" | "preview";
export type UrlStatus = "empty" | "invalid" | "valid";

export type LinkActionIcon =
  | "x-close"
  | "delete"
  | "cancel"
  | "anchor-unlink"
  | "check-mark"
  | "edit"
  | "undo"
  | "refresh"
  | "anchor-link"
  | "history";

export type LinkActionType = "submit" | "button";

export type LinkActionVariant = "destructive" | "neutral" | "positive";

export type LinkActionConfig = {
  readonly type: LinkActionType;
  readonly variant: LinkActionVariant;
  readonly icon: LinkActionIcon;
  readonly tooltip: string;
  readonly ariaLabel: string;
  readonly onClick: () => void;
  readonly disabled?: boolean;
};

export type ActionSet = {
  readonly primary: LinkActionConfig;
  readonly cancel: LinkActionConfig;
};

export interface ActionCallbacks {
  readonly commit: () => void;
  readonly toggleRecents: () => void;
  readonly edit: () => void;
  readonly remove: () => void;
  readonly close: () => void;
}

/* Each allowed scheme is only actionable when its own payload is present, e.g. "https://" and "mailto:" are not. */
const PROTOCOL_PAYLOAD_CHECKS: ReadonlyMap<string, (parsed: URL) => boolean> = new Map([
  ["http:", (parsed: URL) => parsed.hostname !== ""],
  ["https:", (parsed: URL) => parsed.hostname !== ""],
  ["mailto:", (parsed: URL) => parsed.pathname !== ""],
  ["tel:", (parsed: URL) => parsed.pathname !== ""],
  ["file:", (parsed: URL) => parsed.pathname !== "" && parsed.pathname !== "/"],
]);

export const isValidUrl = (value: string): boolean => {
  const candidate = value.trim();
  if (!isSafeHref(candidate)) return false;
  try {
    /* Parsed without a base so relative values such as "example" are rejected. */
    const parsed = new URL(candidate);
    const hasPayload = PROTOCOL_PAYLOAD_CHECKS.get(parsed.protocol);
    if (hasPayload == null) return false;
    return hasPayload(parsed);
  } catch {
    return false;
  }
};

export const deriveDialogMode = (initialUrl: string | null): DialogMode => {
  return initialUrl != null && initialUrl.trim() !== "" ? "preview" : "edit";
};

export const deriveUrlStatus = (value: string): UrlStatus => {
  if (value.trim() === "") return "empty";
  if (isValidUrl(value)) return "valid";
  return "invalid";
};

export const buildActionSet = (mode: DialogMode, status: UrlStatus, callbacks: ActionCallbacks): ActionSet => {
  const { commit, edit, remove, close } = callbacks;
  if (mode === "edit") {
    const cancel: LinkActionConfig = {
      type: "button",
      variant: "neutral",
      icon: "x-close",
      tooltip: "Close",
      ariaLabel: "Cancel",
      onClick: close,
    };
    return {
      primary: {
        type: "submit",
        variant: "positive",
        icon: "check-mark",
        tooltip: "Save",
        ariaLabel: "Save Link",
        onClick: commit,
        disabled: status === "empty",
      },
      cancel,
    };
  }

  const unlink: LinkActionConfig = {
    type: "button",
    variant: "destructive",
    icon: "delete",
    tooltip: "Remove link",
    ariaLabel: "Remove Link",
    onClick: remove,
  };
  return {
    primary: { type: "button", variant: "neutral", icon: "edit", tooltip: "Edit URL", ariaLabel: "Edit URL", onClick: edit },
    cancel: unlink,
  };
};

const VARIANT_CLASS: Record<LinkActionVariant, string> = {
  destructive: CSS.destructive,
  neutral: CSS.neutral,
  positive: CSS.positive,
};

export const renderActionButton = (action: LinkActionConfig | undefined) => {
  if (!action) return null;

  const isSubmit = action.type === "submit";
  return (
    <Tooltip content={action.tooltip} alignment="top">
      {(ref) => (
        <button
          ref={ref as (el: HTMLButtonElement | null) => void}
          type={isSubmit ? "submit" : "button"}
          className={classNames(CSS.ToolbarButton, CSS.PopoverActionButton, VARIANT_CLASS[action.variant])}
          aria-label={action.ariaLabel}
          disabled={action.disabled}
          onClick={isSubmit ? undefined : action.onClick}
        >
          <LinkActionGlyph name={action.icon} />
        </button>
      )}
    </Tooltip>
  );
};
